import { faCalendar } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as moment from "moment";
import { FC, createRef, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import HasPermission from "../../../core/guards/has-permission";
import CustomHighChartComponent from "../../../shared/custom-highchart-component/custom-highchart.component";
import CustomSpinnerComponent from "../../../shared/custom-spinner/custom-spinner.component";
import FilterFormComponent, {
  FilterFormConfig,
} from "../../../shared/filter-form/filter-form.component";
import { assetsByBlock } from "../../../store/slices/asset.slice";
import { downloadEnvironmentalReportsFile } from "../../../store/slices/fault-module/environmental-report.slice";
import { getBlocksBySiteCode } from "../../../store/slices/location-block.slice";
import { getSiteByTCCode } from "../../../store/slices/location-site.slice";
import { getTownCouncils } from "../../../store/slices/town-council.slice";

import { B2Button } from "@hdb/biome-2";
import pdfIcon from "../../../assets/images/pdfBiomeIcon.svg";
import spreadsheetIcon from "../../../assets/images/spreadsheetIconBiome.svg";
import { generateFilenameWithTimestamp } from "src/shared/helpers/generateFilenameWithTimestamp";

interface EnvironmentalReportTableComponentProps {
  chartData: { assetIds: []; hours: []; assetReadings: [] };
  onRefetchData: (
    tcCode: any,
    siteCode: any,
    blockCode: any,
    reportType: any,
    startDate: any,
    endDate: any,
    asset: any
  ) => void;
  isLoading: boolean;
}

const EnvironmentalReportTableComponent: FC<
  EnvironmentalReportTableComponentProps
> = ({ chartData, onRefetchData, isLoading }) => {
  const formRef = createRef();
  const [tcOptions, setTCOptions] = useState<any>([]);
  const [siteOptions, setSiteOptions] = useState<any>([]);
  const [blockOptions, setBlockOptions] = useState<any>([]);
  const [assetOptions, setAssetOptions] = useState<any>([]);
  const [selectedTC, setSelectedTC] = useState<any>(null);
  const [selectedSite, setSelectedSite] = useState<any>(null);
  const [selectedBlock, setSelectedBlock] = useState<any>(null);
  const [isFiltered, setIsFiltered] = useState<any>(false);
  const [selectedReportType, setSelectedReportType] = useState<any>(null);
  const [endDateForm, setEndDateForm] = useState<string>("");
  const [downloaDocumentPayload, setDownloaDocumentPayload] = useState<any>({
    townCouncil: "",
    site: "",
    block: "",
    reportType: "",
    startDate: "",
    endDate: "",
    asset: "",
  });
  const [isAssetSearch, setIsAssetSearch] = useState<boolean>(true);
  const [blockChangeDetected, setBlockChangeDetected] = useState(0);

  const [filterUpdateCount, setFilterUpdateCount] = useState(0);

  const reportTypeOptions = [
    {
      label: "Daylight",
      value: 1,
    },
    {
      label: "Temperature",
      value: 2,
    },
    {
      label: "Humidity",
      value: 3,
    },
    {
      label: "Rainfall",
      value: 4,
    },
  ];

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getTownCouncils({ PageNumber: 0, pageSize: 0 }));
  }, [dispatch]);

  const townCouncilData = useSelector((state: any) => {
    return state.townCouncil.townCouncils;
  });

  const siteDataByTCCode = useSelector((state: any) => {
    return state.locationSite.sitesByTCCode;
  });

  const blockDataSiteCode = useSelector((state: any) => {
    return state.locationBlock.blocksBySiteCode;
  });

  const assetsData = useSelector((state: any) => {
    return state.asset.selectedAssetsByBlock;
  });

  const isLoadingAssetsByGroup = useSelector((state: any) => {
    return state.asset.isLoadingAssetsByGroup;
  });

  const isLoadingTownCouncils = useSelector((state: any) => {
    return state.townCouncil.isLoadingTownCouncils;
  });

  const isLocationSitesByTCCode = useSelector((state: any) => {
    return state.locationSite.isLocationSitesByTCCode;
  });

  const isLoadingBlocksBySiteCode = useSelector((state: any) => {
    return state.locationBlock.isLoadingBlocksBySiteCode;
  });

  const isLoadingEnvironmentalReportsDownload = useSelector((state: any) => {
    return state.environmentalReport.isLoadingEnvironmentalReportsDownload;
  });

  useEffect(() => {
    const assetIDs =
      assetsData?.map(
        (asset: { assetCode: string; sensorDisplayName: string }) => ({
          label: asset.sensorDisplayName,
          value: asset.assetCode,
        })
      ) || [];

    if (assetIDs.length > 0) {
      assetIDs.unshift({
        label: "All",
        value: null,
      });
    }

    setAssetOptions(assetIDs);

    if (assetsData?.length > 0) {
      setIsAssetSearch(false);
    }
  }, [assetsData]);
  const [currentTC, setCurrentTC] = useState({ label: "", value: "" });

  useEffect(() => {
    const tcs = townCouncilData?.data?.map((tc: any) => ({
      label: tc?.name,
      value: tc?.tcCode,
    }));
    // if user is TC Admin only 1 tc is available
    if (tcs?.length === 1) {
      setCurrentTC(tcs[0]);
    }

    setTCOptions(tcs);
    setFilterUpdateCount(filterUpdateCount + 1);
  }, [townCouncilData]);

  useEffect(() => {
    if (currentTC.value.length) {
      setFormFilterInitialValues((prev) => ({
        ...prev,
        townCouncil: currentTC.value,
      }));

      dispatch(getSiteByTCCode({ tcCode: currentTC.value }));
    }
  }, [currentTC]);

  useEffect(() => {
    const sites = siteDataByTCCode?.data?.map((site: any) => ({
      label: site?.name,
      value: site?.siteCode,
    }));

    if (!sites?.length) return;

    setSiteOptions(sites);
  }, [siteDataByTCCode]);

  useEffect(() => {
    const blocks = blockDataSiteCode?.data?.map((block: any) => ({
      label: block?.blockName,
      value: block?.blockCode,
    }));

    setBlockOptions(blocks);
  }, [blockDataSiteCode]);

  useEffect(() => {
    if (selectedTC && selectedSite && selectedBlock) {
      // dispatch(
      //   getAssetsByGroup({
      //     tcCode: selectedTC,
      //     siteCode: selectedSite,
      //     blockCode: selectedBlock,
      //   })
      // );
      dispatch(assetsByBlock({ blockCode: selectedBlock }));
    }
  }, [selectedBlock]);

  const onChangeTC = (selectedTC: any) => {
    setSiteOptions([]);
    setBlockOptions([]);
    setSelectedTC(selectedTC);
    setBlockChangeDetected(blockChangeDetected + 1);

    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      townCouncil: selectedTC,
      site: "",
      block: "",
      asset: "",
    }));

    if (!selectedTC) return;

    dispatch(getSiteByTCCode({ tcCode: selectedTC }));
    // onChangeLocation();
  };

  const onChangeSite = (selectedSite: any) => {
    setBlockOptions([]);
    setSelectedSite(selectedSite);

    setBlockChangeDetected(blockChangeDetected + 1);
    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      site: selectedSite,
      block: "",
      asset: "",
    }));

    if (!selectedSite) return;
    dispatch(getBlocksBySiteCode({ siteCode: selectedSite }));

    // onChangeLocation();
  };

  const onChangeBlock = (selectedBlock: any) => {
    setAssetOptions([]);
    setSelectedBlock(selectedBlock);

    setBlockChangeDetected(blockChangeDetected + 1);
    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      block: selectedBlock,
      asset: "",
    }));
    if (!selectedBlock) return;

    // onChangeLocation(selectedBlock);
  };

  const onChangeStartDate = (startDate: any) => {
    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      startDate: startDate,
    }));
  };

  const onChangeEndDate = (endDate: any) => {
    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      endDate: endDate,
    }));
  };

  const onChangeReportType = (type: any) => {
    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      reportType: type,
    }));
  };

  const onChangeAsset = (selectedAsset: any) => {
    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      asset: selectedAsset,
    }));
  };

  // const onTypeAheadInputChange = (value: string) => {
  //   if (value.length) {
  //     let searchText = value;

  //     if (selectedTC && selectedSite && selectedBlock) {
  //       dispatch(
  //         getAssetsByGroup({
  //           tcCode: selectedTC,
  //           siteCode: selectedSite,
  //           blockCode: selectedBlock,
  //           searchText: searchText || "",
  //         })
  //       );
  //     }
  //   }
  // };

  useEffect(() => {
    return () => {
      setSelectedReportType(null);
    };
  }, []);

  const onSubmitFilter = () => {
    const form: any = formRef.current;

    if (form) {
      form.handleSubmit();

      const {
        townCouncil,
        site,
        block,
        reportType,
        startDate,
        endDate,
        asset,
      } = form.values;

      if (endDate) {
        if (endDate < startDate) {
          setIsFiltered(false);
          return;
        }
      }

      setEndDateForm(endDate);
      if (reportType && startDate && townCouncil && site && block) {
        setSelectedReportType(reportType);
      } else {
        return;
      }

      onRefetchData(
        townCouncil ? townCouncil : null,
        site ? site : null,
        block ? block : null,
        reportType ? reportType : null,
        startDate ? moment.utc(startDate) : null,
        endDate ? moment.utc(endDate) : null,
        asset ? asset : null
      );

      setDownloaDocumentPayload({
        townCouncil,
        site,
        block,
        reportType,
        startDate,
        endDate,
        asset,
      });

      setIsFiltered(true);
    }
  };

  const onClickDownload = (fileType: any) => {
    const form: any = formRef.current;

    if (form) {
      // form.handleSubmit();

      const {
        townCouncil,
        site,
        block,
        reportType,
        startDate,
        endDate,
        asset,
      } = downloaDocumentPayload;

      if (!reportType || !startDate || !townCouncil || !site || !block) {
        return;
      }

      dispatch(
        downloadEnvironmentalReportsFile({
          fileType,
          tcCode: townCouncil ? townCouncil : null,
          siteCode: site ? site : null,
          blockCode: block ? block : null,
          reportType: reportType ? reportType : null,
          startDate: startDate ? moment.utc(startDate) : null,
          endDate: endDate ? moment.utc(endDate) : null,
          asset: asset ? asset : null,
        })
      );
    }
  };

  const [formFilterInitialValues, setFormFilterInitialValues] = useState({
    townCouncil: "",
    site: "",
    block: "",
    reportType: "",
    startDate: "",
    endDate: "",
    asset: "",
  });

  const filterActionConfig: any = {
    label: "Search",
    event: onSubmitFilter,
    variant: "primary",
  };

  const onReset = () => {
    const form: any = formRef.current;
    form.resetForm();

    setFilterUpdateCount(filterUpdateCount + 1);

    setAssetOptions([]);
    setSiteOptions([]);
    setBlockOptions([]);
    setIsFiltered(false);
    setIsAssetSearch(true);
    setSelectedBlock(null);
    setFormFilterInitialValues({
      townCouncil: "",
      site: "",
      block: "",
      reportType: "",
      startDate: "",
      endDate: "",
      asset: "",
    });
  };

  const reset: any = {
    label: "Reset",
    event: onReset,
    variant: "secondary",
  };
  const currentDateTime = moment().format("YYYY-MM-DD");
  const date13MonthsAgo = moment().subtract(13, "months").format("YYYY-MM-DD");
  const yesterdayDateTime = moment().subtract(1, "day").format("YYYY-MM-DD");

  const filterFormConfig: FilterFormConfig = {
    controls: [
      {
        controlGroup: [
          {
            name: "townCouncil",
            placeholder: "Select Town Council *",
            type: "select",
            options: tcOptions,
            onChange: onChangeTC,
            required: true,
            isLoading: isLoadingTownCouncils,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "site",
            placeholder: "Select Site *",
            type: "select",
            options: siteOptions,
            onChange: onChangeSite,
            required: true,
            isLoading: isLocationSitesByTCCode,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "block",
            placeholder: "Select Block *",
            type: "select",
            options: blockOptions,
            onChange: onChangeBlock,
            required: true,
            isLoading: isLoadingBlocksBySiteCode,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "reportType",
            placeholder: "Select Report Type *",
            type: "select",
            onChange: onChangeReportType,
            options: reportTypeOptions,
            required: true,
            isLoading: false,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "startDate",
            placeholder: "Select Start Date *",
            type: "date",
            required: true,
            isLoading: false,
            minDate: date13MonthsAgo,
            maxDate: yesterdayDateTime,
            onChange: onChangeStartDate,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "endDate",
            placeholder: "Select End Date",
            type: "date",
            required: true,
            isLoading: false,
            minDate: date13MonthsAgo,
            maxDate: yesterdayDateTime,
            onChange: onChangeEndDate,
          },
        ],
      },
      // {
      //   controlGroup: [
      //     {
      //       name: "asset",
      //       placeholder: "Select Asset",
      //       type: "typeahead",
      //       required: false,
      //       selectType: "single",
      //       selectOptions: assetOptions,
      //       onInputChange: onTypeAheadInputChange,
      //       isLoading: true,
      //       disabled: isAssetSearch,
      //     },
      //   ],
      // },
      {
        controlGroup: [
          {
            name: "asset",
            placeholder: "Select Asset",
            type: "searchable-select",
            required: false,
            selectType: "single",
            options: assetOptions,
            onChange: onChangeAsset,
            isLoading: true,
            disabled: isAssetSearch,
            resetSearchableSelect: blockChangeDetected,
          },
        ],
      },
    ],
  };

  const navigate = useNavigate();

  const redirectToConfiguration = () => {
    navigate("/admin/configuration/reportconfiguration");
  };

  useEffect(() => {
    setSiteOptions([]);
    setBlockOptions([]);
    setAssetOptions([]);
    setIsAssetSearch(true);
  }, []);

  const validationSchema = yup.object().shape({
    townCouncil: yup.string().required("Town Council is required"),
    site: yup.string().required("Site is required"),
    block: yup.string().required("Block is required"),
    reportType: yup.number().required("Report Type is required"),
    startDate: yup.date().required("Start Date is required"),
    endDate: yup
      .date()
      .min(yup.ref("startDate"), "End Date must be after Start Date"),

    // endDate: yup.date().required("Date is required"),
    // asset: yup.string().required("Asset is required"),
  });

  return (
    <div>
      {/*       <div className="flex justify-end table-generic-action mb-2">
        <B2Button
          className="icon-text-btn"
          variant="primary"
          onClick={redirectToConfiguration}
        >
          Automatic Report Scheduler
          <FontAwesomeIcon icon={faCalendar} className="fs-7 ms-2" />
        </B2Button>
      </div> */}

      <div>
        <FilterFormComponent
          key={formFilterInitialValues?.townCouncil}
          formRef={formRef}
          initialValues={formFilterInitialValues}
          formConfig={filterFormConfig}
          actionConfig={filterActionConfig}
          resetButton={reset}
          validation={validationSchema}
          filterUpdateCount={filterUpdateCount}
        />
      </div>
      <div>
        <CustomSpinnerComponent
          height={"400px"}
          width={"100%"}
          isLoading={isLoading}
        >
          {selectedReportType && isFiltered && (
            <div className="my-3">
              <div className="export py-4 d-flex align-items-center justify-content-end">
                <HasPermission
                  moduleName="Environmental Report"
                  actionName="Download-Environmental Report"
                  isRoute={false}
                >
                  <div className="d-flex gap-2">
                    <B2Button
                      variant="light"
                      onClick={() => onClickDownload("downloadPdf")}
                    >
                      <img className="fs-7 ms-2" src={pdfIcon} alt="PDF Icon" />{" "}
                      Export as PDF
                    </B2Button>

                    <B2Button
                      variant="light"
                      onClick={() => onClickDownload("downloadExcel")}
                    >
                      <img
                        className="fs-7 me-2"
                        src={spreadsheetIcon}
                        alt="Spreadsheet Icon"
                      />{" "}
                      Export as Excel
                    </B2Button>
                  </div>

                  {isLoadingEnvironmentalReportsDownload ? (
                    <Spinner
                      className="ms-2"
                      style={{ color: "#1976D2" }}
                      animation="border"
                      role="status"
                      size="sm"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </Spinner>
                  ) : null}
                </HasPermission>
              </div>

              {selectedReportType == 1 ? (
                <CustomHighChartComponent
                  chartTitle="Daylight"
                  xAxisCategories={chartData?.hours}
                  SeriesType={"line"}
                  seriesData={chartData?.assetReadings}
                  seriesShowInLegend={false}
                  seriesName={"Daylight"}
                  xAxisTitleText={endDateForm ? "Day" : "Hours"}
                  yAxisTitleText={"Daylight"}
                  colors={["#3B1DCD"]}
                  // baseLine={[40000]}
                  fileName={generateFilenameWithTimestamp("Daylight")}
                  chartName={"Daylight"}
                />
              ) : selectedReportType == 2 ? (
                <CustomHighChartComponent
                  chartTitle="Temperature"
                  xAxisCategories={chartData?.hours}
                  SeriesType={"line"}
                  seriesData={chartData?.assetReadings}
                  seriesShowInLegend={false}
                  seriesName={"Temperature"}
                  xAxisTitleText={endDateForm ? "Day" : "Hours"}
                  yAxisTitleText={"Temperature"}
                  colors={["#3B1DCD"]}
                  // baseLine={[40000]}
                  fileName={generateFilenameWithTimestamp("Temperature")}
                  chartName={"Temperature"}
                />
              ) : selectedReportType == 3 ? (
                <CustomHighChartComponent
                  chartTitle="Humidity"
                  xAxisCategories={chartData?.hours}
                  SeriesType={"line"}
                  seriesData={chartData?.assetReadings}
                  seriesShowInLegend={false}
                  seriesName={"Humidity"}
                  xAxisTitleText={endDateForm ? "Day" : "Hours"}
                  yAxisTitleText={"Humidity"}
                  colors={["#3B1DCD"]}
                  // baseLine={[40000]}
                  fileName={generateFilenameWithTimestamp("Humidity")}
                  chartName={"Humidity"}
                />
              ) : (
                <CustomHighChartComponent
                  chartTitle="Rainfall"
                  xAxisCategories={chartData?.hours}
                  SeriesType={"line"}
                  seriesData={chartData?.assetReadings}
                  seriesShowInLegend={false}
                  seriesName={"Rainfall"}
                  xAxisTitleText={endDateForm ? "Day" : "Hours"}
                  yAxisTitleText={"Rainfall"}
                  colors={["#3B1DCD"]}
                  // baseLine={[40000]}
                  fileName={generateFilenameWithTimestamp("Rainfall")}
                  chartName={"Rainfall"}
                />
              )}
            </div>
          )}
        </CustomSpinnerComponent>
      </div>
    </div>
  );
};

export default EnvironmentalReportTableComponent;
